import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useRef } from 'react';
import { NotificationManagerContext, } from './NotificationManagerContext';
const DEFAULT_POLL_INTERVAL = 1000;
const SHORT_OPERATION_MAX_DURATION_MS = 15_000;
const SHORT_OPERATION_SUCCESS_TOAST_AUTO_CLOSE_MS = 7_000;
function formatDurationMs(durationMs) {
    const totalSeconds = Math.round(durationMs / 1000);
    if (totalSeconds < 60) {
        if (durationMs < 1000) {
            return '< 1 s';
        }
        return `${totalSeconds} s`;
    }
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes} min ${String(seconds).padStart(2, '0')} s`;
}
function resolveSuccessAutoClose(status) {
    const durationMs = status.durationMs;
    if (typeof durationMs !== 'number') {
        return false;
    }
    return durationMs < SHORT_OPERATION_MAX_DURATION_MS ? SHORT_OPERATION_SUCCESS_TOAST_AUTO_CLOSE_MS : false;
}
function resolveErrorAutoClose() {
    return SHORT_OPERATION_SUCCESS_TOAST_AUTO_CLOSE_MS;
}
function resolveHideProgressBar(autoClose) {
    return typeof autoClose === 'number' ? false : true;
}
export function NotificationManagerProvider({ operationStatusApiCall, toast, onRefresh, renderDuration, children, }) {
    const activeOperationsRef = useRef(new Set());
    const trackOperation = useCallback((opts) => {
        const { operationId, successToast, processingToast, errorToast, pollInterval, onSuccess, onVerified, isWarning } = opts;
        const interval = pollInterval ?? DEFAULT_POLL_INTERVAL;
        if (activeOperationsRef.current.has(operationId)) {
            return;
        }
        activeOperationsRef.current.add(operationId);
        let onVerifiedCalled = false;
        if (processingToast) {
            toast(processingToast, { type: 'info', autoClose: 2000, hideProgressBar: false });
        }
        const poll = async () => {
            try {
                const status = await operationStatusApiCall(operationId);
                if (status.verified && onVerified && !onVerifiedCalled) {
                    onVerifiedCalled = true;
                    onVerified(status);
                }
                if (status.finished) {
                    activeOperationsRef.current.delete(operationId);
                    onSuccess?.(status);
                    onRefresh?.();
                    const content = typeof successToast === 'function' ? successToast(status) : successToast;
                    const durationMs = status.durationMs;
                    const formattedDuration = typeof durationMs === 'number' ? formatDurationMs(durationMs) : null;
                    const contentWithDuration = typeof durationMs === 'number' ? (_jsxs("div", { children: [_jsx("div", { children: content }), _jsx("div", { style: { marginTop: 4, fontSize: '0.8rem' }, children: renderDuration
                                    ? renderDuration({ durationMs, formattedDuration: formattedDuration, status })
                                    : formattedDuration })] })) : (content);
                    const autoClose = resolveSuccessAutoClose(status);
                    toast(contentWithDuration, {
                        type: isWarning?.(status) ? 'warning' : 'success',
                        autoClose,
                        hideProgressBar: resolveHideProgressBar(autoClose),
                    });
                }
                else {
                    setTimeout(poll, interval);
                }
            }
            catch (error) {
                activeOperationsRef.current.delete(operationId);
                console.error('NotificationManager polling error:', error);
                onRefresh?.();
                const content = errorToast ?? 'Operation failed. Please try again.';
                const autoClose = resolveErrorAutoClose();
                toast(content, { type: 'error', autoClose, hideProgressBar: resolveHideProgressBar(autoClose) });
            }
        };
        setTimeout(poll, interval);
    }, [operationStatusApiCall, toast, onRefresh]);
    const value = useMemo(() => ({ trackOperation }), [trackOperation]);
    return _jsx(NotificationManagerContext.Provider, { value: value, children: children });
}
//# sourceMappingURL=NotificationManagerProvider.js.map